import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IDepartment,
  IRole,
  IUpdatePassword,
  IUser,
  IUserValidation,
  IFunctionalityByDepartment,
  IFunctionality,
  IAssignFunctionality,
  IUserProfile,
  IUserProfileInput,
  IAssignFunctionalityDep,
  IFactorySection,
} from "../model/user";
import { EUserEndPoint, userApi } from "../services/userApi";
import { IStandardResponse } from "../../orders/models/order";

export const USERS_QUERY_KEY = "users/all";

const DEPARTMENT_KEY = "department";
export const DEPARTMENT_FUNCTIONALITY_KEY = "department_functionality";
export const USER_PROFILE_QUERY_KEY = "users/all";

interface UseUsersOptions {
  global_search?: string;
  queryOptions?: {
    onSuccess?: (data: IStandardResponse<Array<IUser>>) => void;
  };
}

interface GetUsersOptions {
  id_user: number;
  queryOptions?: {
    enabled?: boolean;
    retry?: boolean | number;
    onSuccess?: (data: IStandardResponse<IUser>) => void;
  };
}

interface UpdateUserVariables {
  id_user: number;
  user: Partial<IUser>;
}

interface UpdateProfileVariables {
  id_user: number;
  user: Partial<IUser>;
  profile: IUserProfileInput | undefined;
}

interface ValidateUserVariables {
  id_user: number;
  validationData: IUserValidation;
}

interface UpdatePasswordVariables {
  id_user: number;
  passwordData: IUpdatePassword;
}

interface AssignFuncsVariables {
  id_department: number;
  funcsData: IAssignFunctionality[];
}

interface UpdateAssignFuncsVariables {
  id_department: number;
  funcsData: IAssignFunctionalityDep[];
}

interface GetFunctionalityByDepVariables {
  id_department: number;
  enabled: boolean;
  queryOptions?: {
    onSuccess?: (data: IStandardResponse<Array<IUser>>) => void;
  };
}

export const useRoles = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IStandardResponse<Array<IRole>>
  >([EUserEndPoint.ROLE]);

  const query = useQuery<IStandardResponse<Array<IRole>>, Error>({
    queryKey: [EUserEndPoint.ROLE],
    queryFn: () => userApi.getRoles(),
    placeholderData: previousData,
    staleTime: 5 * 60 * 1000,
  });

  const { data } = query || {};

  return {
    ...query,
    data: data,
  };
};

export const useUsers = ({ global_search, queryOptions }: UseUsersOptions) => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IStandardResponse<Array<IUser>>
  >([`${USERS_QUERY_KEY}`]);

  const query = useQuery<IStandardResponse<Array<IUser>>, Error>({
    queryKey: [`${USERS_QUERY_KEY}`],
    queryFn: () => userApi.getUsers(global_search),
    placeholderData: previousData,
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
  });

  const { data } = query || {};

  return {
    ...query,
    data: data,
  };
};

export const useValidateUser = () => {
  return useMutation<
    IStandardResponse<{ message: string }>,
    Error,
    ValidateUserVariables
  >({
    mutationFn: ({ id_user, validationData }) =>
      userApi.validateUser(id_user, validationData),
  });
};

export const useUpdateUser = () => {
  return useMutation<IStandardResponse<IUser>, Error, UpdateUserVariables>({
    mutationFn: ({ id_user, user }) => userApi.updateUser(id_user, user),
  });
};

export const useUpdateProfile = () => {
  return useMutation<IStandardResponse<IUser>, Error, UpdateProfileVariables>({
    mutationFn: ({ id_user, user, profile }) =>
      userApi.updateProfile(id_user, { user, profile }),
  });
};

export const useDisableUser = () => {
  return useMutation<IStandardResponse<unknown>, Error, { id_user: number }>({
    mutationFn: ({ id_user }) => userApi.disableUser(id_user),
  });
};

export const useEnableUser = () => {
  return useMutation<IStandardResponse<unknown>, Error, { id_user: number }>({
    mutationFn: ({ id_user }) => userApi.enableUser(id_user),
  });
};

export const useGetUserById = ({ id_user, queryOptions }: GetUsersOptions) => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<IStandardResponse<IUser>>([
    `${USERS_QUERY_KEY}`,
    id_user,
  ]);

  const query = useQuery<IStandardResponse<IUser>, Error>({
    queryKey: [`${USERS_QUERY_KEY}`, id_user],
    queryFn: () => userApi.getUserById(id_user),
    placeholderData: previousData,
    staleTime: 5 * 60 * 1000,
    retry: false,
    ...queryOptions,
    enabled: !!id_user && (queryOptions?.enabled ?? true),
  });

  const { data } = query || {};

  return {
    ...query,
    data: data,
  };
};

export const useGetUserProfile = ({
  id_user,
  queryOptions,
}: GetUsersOptions) => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IStandardResponse<IUserProfile>
  >([`${USER_PROFILE_QUERY_KEY}`, id_user]);

  const query = useQuery<IStandardResponse<IUserProfile>, Error>({
    queryKey: [`${USER_PROFILE_QUERY_KEY}`, id_user],
    queryFn: () => userApi.getUserProfile(id_user),
    placeholderData: previousData,
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
  });

  const { data } = query || {};

  return {
    ...query,
    data: data,
  };
};

export const useGetUserBySection=(id_section:number)=>{

 const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IStandardResponse<Array<IUser>>
  >([USER_PROFILE_QUERY_KEY]);

  const query = useQuery<IStandardResponse<Array<IUser>>, Error>({
    queryKey: [DEPARTMENT_KEY],
    queryFn: () => userApi.getUserBySection(id_section),
    placeholderData: previousData,
    staleTime: 5 * 60 * 1000,
  });

  const { data } = query || {};

  return {
    ...query,
    data: data,
  };
}
export const useUpdatePassword = () => {
  return useMutation<IStandardResponse<IUser>, Error, UpdatePasswordVariables>({
    mutationFn: ({ id_user, passwordData }) =>
      userApi.updatePassword(id_user, passwordData),
  });
};

export const useResetPassword = () => {
  return useMutation<IStandardResponse<{ message: string }>,Error,string>({
    mutationFn: (matricule: string) => userApi.resetPassword(matricule),
  });
};

export const useGetFunctionalityByDep = ({
  id_department,
  enabled = true,
  queryOptions,
}: GetFunctionalityByDepVariables) => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IStandardResponse<Array<IFunctionalityByDepartment>>
  >([`${DEPARTMENT_FUNCTIONALITY_KEY}`, id_department]);

  const query = useQuery<
    IStandardResponse<Array<IFunctionalityByDepartment>>,
    Error
  >({
    queryKey: [`${DEPARTMENT_FUNCTIONALITY_KEY}`, id_department],
    queryFn: () => userApi.getFunctionalityByDepartment(id_department),
    placeholderData: previousData,
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!id_department,
    ...queryOptions,
  });

  const { data } = query || {};

  return {
    ...query,
    data: data,
  };
};

export const useFunctionality = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IStandardResponse<Array<IFunctionality>>
  >([EUserEndPoint.FUNCTIONALITY]);

  const query = useQuery<IStandardResponse<Array<IFunctionality>>, Error>({
    queryKey: [EUserEndPoint.FUNCTIONALITY],
    queryFn: () => userApi.getFunctionalities(),
    placeholderData: previousData,
    staleTime: 5 * 60 * 1000,
  });

  const { data } = query || {};

  return {
    ...query,
    data: data,
  };
};

export const useDepartments = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IStandardResponse<Array<IDepartment>>
  >([DEPARTMENT_KEY]);

  const query = useQuery<IStandardResponse<Array<IDepartment>>, Error>({
    queryKey: [DEPARTMENT_KEY],
    queryFn: () => userApi.getDepartments(),
    placeholderData: previousData,
    staleTime: 5 * 60 * 1000,
  });

  const { data } = query || {};

  return {
    ...query,
    data: data,
  };
};

export const useFactorySection = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IStandardResponse<Array<IFactorySection>>
  >([EUserEndPoint.FACTORY_SECTION]);

  const query = useQuery<IStandardResponse<Array<IFactorySection>>, Error>({
    queryKey: [[EUserEndPoint.FACTORY_SECTION]],
    queryFn: () => userApi.getFactorySection(),
    placeholderData: previousData,
    staleTime: 5 * 60 * 1000,
  });
  const { data } = query || {};
  return {
    ...query,
    data: data,
  };
};

export const useAssignFunctionality = () => {
  return useMutation<IStandardResponse<unknown>, Error, AssignFuncsVariables>({
    mutationFn: ({ id_department, funcsData }) =>
      userApi.assignFunctionality(id_department, funcsData),
  });
};

export const useUpdateAssignFunctionality = () => {
  return useMutation<
    IStandardResponse<unknown>,
    Error,
    UpdateAssignFuncsVariables
  >({
    mutationFn: ({ id_department, funcsData }) =>
      userApi.updateAssignFunctionality(id_department, funcsData),
  });
};

export const useRemoveFunctionality = () => {
  return useMutation<IStandardResponse<unknown>, Error, AssignFuncsVariables>({
    mutationFn: ({ id_department, funcsData }) =>
      userApi.removeFunctionality(id_department, funcsData),
  });
};

export const useLogout = () => {
  return useMutation<IStandardResponse<unknown>, Error>({
    mutationFn: () => userApi.logout(),
  });
};
